import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import IconCode from '@/assets/icons/icon-source-code.svg?react';

interface NodeEmbedPlaceholderProps {
  isActive?: boolean;
}

const NodeEmbedPlaceholder: React.FC<NodeEmbedPlaceholderProps> = ({ isActive = false }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [showText, setShowText] = useState(true);
  const [iconSizePercent, setIconSizePercent] = useState(38.5);
  const [corner, setCorner] = useState<{ rx: number; ry: number }>({ rx: 0, ry: 0 });
  const [hovered, setHovered] = useState(false);

  const getIconSizePercent = (width: number) => {
    if (width < 158) return 90;
    if (width < 175) return 75;
    if (width < 257) return 50;
    return 38.5;
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        setShowText(width >= 257);
        setIconSizePercent(getIconSizePercent(width));
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const measure = () => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      if (!width || !height) return;
      const cs = getComputedStyle(el);
      const br = parseFloat(cs.borderTopLeftRadius || '0') || 0;
      setCorner({ rx: (br / width) * 100, ry: (br / height) * 100 });
    };
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(el);
    measure();
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className={clsx(
        'relative flex h-full w-full flex-col items-center justify-center gap-3 overflow-hidden',
        isActive && 'pointer-events-none',
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        ref={innerRef}
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{ borderRadius: 'inherit' }}
      >
        <svg
          className={clsx(
            'pointer-events-none absolute inset-0 h-full w-full stroke-neutral-200 transition-all duration-300',
            (isActive || hovered) && 'stroke-indigo-500',
          )}
          fill="none"
          strokeWidth={1}
        >
          <rect
            width="100%"
            height="100%"
            rx={`${corner.rx}%`}
            ry={`${corner.ry}%`}
            strokeDasharray="8 4"
            strokeDashoffset="0"
            vectorEffect="non-scaling-stroke"
            pathLength="100"
            style={{
              fill: 'rgba(250, 250, 250, 1)',
            }}
          />
        </svg>
      </div>
      <IconCode
        className={clsx(
          'relative z-10 text-neutral-300 transition-all duration-300',
          (isActive || hovered) && 'text-indigo-400',
        )}
        style={{ width: `${iconSizePercent}%`, height: `${iconSizePercent}%` }}
      />
      {showText && (
        <span
          className={clsx(
            'text-button-sm relative z-10 text-center font-medium text-neutral-400 transition-all duration-300',
            (isActive || hovered) && 'text-indigo-500',
          )}
        >
          Embed
        </span>
      )}
    </div>
  );
};

export default NodeEmbedPlaceholder;
